import * as Sentry from '@sentry/browser';
import { filter, find, isArray, remove } from 'lodash';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import moment from 'moment';

import { permissions } from '@/constants';
import { PIDAlertsMock } from '@/constants/PIDAlertsMock';
import { AlertLevels, AlertStatus, ComponentTierName, AlertTypes } from '@/constants/enums';
import i18n from '@/i18n/i18n';
import { checkTierPermission, waitFor } from '@/utils/util-functions';
import epaxios from '@/utils/epaxios';
import logger from '@/utils/logger';
import LogErrorMessage from '@/utils/LogErrorMessage';

import RootStore from './RootStore';

enum Severity {
  shutdown = 10,
  warning = 20,
  service = 30,
  internal = 40,
}

const pushBrowserNotification = alert => {
  const title = `${i18n.t('New Alert')} - ${alert.severity}    ${moment(alert.openingTime).format(
    'MMM D[,] Y[,] h:mma',
  )}`;
  const body = `${alert.affectedDevicesNames} - ${alert.title}`;

  if (Notification.permission === 'granted') {
    new Notification(title, { body });
  } else {
    Notification.requestPermission().then(function (permission) {
      if (permission === 'granted') {
        new Notification(title, { body });
      }
    });
  }
};

export default class AlertStore {
  defaultKey = 'active';
  rootStore: typeof RootStore;
  @observable alerts = [];
  @observable currentStationId;
  @observable isLoading = false;
  @observable selectedAlerts = [];

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;

    reaction(
      () => this.rootStore?.stationStore?.currentStation?.id,
      currentStationId => {
        // logger('Alerts refresh with station');
        if (!currentStationId) return this.reset();

        this.currentStationId = currentStationId;
        this.get();
      },
    );
  }

  @action get = async () => {
    this.isLoading = true;

    try {
      await waitFor(() => this.rootStore.stationStore.isLoading === false);
      let alerts;
      if (checkTierPermission(ComponentTierName.PaymentPlan, this.rootStore.stationStore)) {
        alerts = PIDAlertsMock;
      } else {
        const endPointEnding = this.rootStore.permissionStore.isAuth(permissions.VIEW_INTERNAL_SEVERITY_ALERT)
          ? '/internal'
          : '';
        alerts = (await epaxios.get(`/alert-record/station/${this.currentStationId}${endPointEnding}`))?.data;
      }

      if (isArray(alerts)) {
        alerts.forEach(alert => (alert.isRead = true));
        this.alerts = alerts;
      } else {
        this.alerts = [];
      }
    } catch (e) {
      logger(`Alerts error: ${e}`);
    } finally {
      this.isLoading = false;
    }
  };

  // todo: change function name and param name. confusing naming, it is too short and uninformative
  @action find = id => {
    return filter(this.alerts, { deviceId: id });
  };

  @action addSelectedAlert = alert => {
    this.selectedAlerts.push(alert);
  };
  @action removeSelectedAlert = alertToRemove => {
    remove(this.selectedAlerts, alert => {
      return alert.id === alertToRemove.id;
    });
  };
  @action removeUnSelectedAlerts = () => {
    remove(this.selectedAlerts, alert => {
      return alert.isSelected === false;
    });
  };

  @action resetSelectedAlerts = () => {
    this.selectedAlerts.forEach(alert => {
      alert.isSelected = false;
    });
    this.selectedAlerts = [];
  };

  @action alertRecordById = alertRecordId => {
    return find(this.alerts, { id: alertRecordId });
  };

  @action update = async (id, updateAlert) => {
    return await epaxios.put(`/alert-record/station/${this.currentStationId}/${id}`, updateAlert);
  };

  @action updateAcknowledged = async (id, updateAlert) => {
    return await epaxios.patch(`/alert-record/station/${this.currentStationId}/${id}/acknowledged`, updateAlert);
  };

  @action updateAcknowledgedBatch = async payload => {
    return await epaxios.patch(`/alert-record/station/${this.currentStationId}/batch-acknowledge`, payload);
  };

  @action archive = async id => {
    try {
      const { data } = await this.updateAcknowledged(id, { acknowledged: true });
      const oldAlert = find(this.alerts, { id });
      oldAlert && Object.assign(oldAlert, data);
    } catch (e) {
      Sentry.captureException(new Error(`Error when archiving alert: ${e}`));
    }
  };

  @action archiveBatch = async () => {
    try {
      const { data } = await this.updateAcknowledgedBatch({
        acknowledged: true,
        ids: this.selectedAlerts.map(alert => alert.id),
      });
      this.selectedAlerts.forEach(alert => {
        const oldAlert = find(this.alerts, { id: alert.id });
        oldAlert && Object.assign(oldAlert, data);
      });
      this.resetSelectedAlerts();
    } catch (e) {
      Sentry.captureException(new Error(`Error when archiving batch alerts: ${e}`));
    }
  };

  @action unArchive = async id => {
    try {
      const { data } = await this.updateAcknowledged(id, { acknowledged: false });
      const oldAlert = find(this.alerts, { id });
      oldAlert && Object.assign(oldAlert, data);
    } catch (e) {
      LogErrorMessage('Alert unarchive error. Server status code ', e);
      return Promise.reject(e);
    }
  };

  @action updateAlertFromServer = (newAlert: { id?: string } = {}) => {
    const oldAlert = find(this.alerts, { id: newAlert.id }) || {};
    Object.assign(oldAlert, newAlert);
  };

  @action addAlertFromServer = newAlert => {
    if (!newAlert) return;
    newAlert.isRead = false;
    this.alerts.push(newAlert);
    pushBrowserNotification(this.alertRecordById(newAlert.id));
  };

  // set all alerts of a certain alerts tab as read.
  // if no tab name is passed - all alerts are set as read
  @action setIsRead = tabName => {
    switch (tabName) {
      case AlertTypes.Active:
        return this.activeAlerts.forEach(alert => (alert.isRead = true));

      case AlertLevels.Shutdown:
        return this.shutdownAlerts.forEach(alert => (alert.isRead = true));

      case AlertLevels.Warning:
        return this.warningAlerts.forEach(alert => (alert.isRead = true));

      case AlertLevels.Service:
        return this.serviceAlerts.forEach(alert => (alert.isRead = true));

      case AlertTypes.Archived:
        return this.archivedAlerts.forEach(alert => (alert.isRead = true));

      default:
        return this.alerts.forEach(alert => (alert.isRead = true));
    }
  };

  @action reset() {
    this.alerts = [];
    this.currentStationId = undefined;
    this.isLoading = false;
  }

  @computed get sortedAlerts() {
    // for active(open) alert records, sort by severity, then by opening time, then by acknowledged
    const activeAlertRecordSortingLogic = (alertRecord1, alertRecord2) => {
      if (alertRecord1.severity === alertRecord2.severity) {
        if (alertRecord1.openingTime === alertRecord2.openingTime) {
          if (alertRecord1.acknowledged === alertRecord2.acknowledged) return -1;
          else return alertRecord1.acknowledged ? 1 : -1;
        } else return alertRecord1.openingTime > alertRecord2.openingTime ? -1 : 1;
      } else return Severity[alertRecord1.severity] < Severity[alertRecord2.severity] ? -1 : 1;
    };
    // for inactive(closed) alert records, sort by opening time, then by acknowledged
    const inactiveAlertRecordSortingLogic = (alertRecord1, alertRecord2) => {
      if (alertRecord1.lastUpdated === alertRecord2.lastUpdated) {
        if (alertRecord1.acknowledged === alertRecord2.acknowledged) return -1;
        else return alertRecord1.acknowledged ? 1 : -1;
      } else return alertRecord1.lastUpdated > alertRecord2.lastUpdated ? -1 : 1;
    };
    const activeAlertRecords = filter(this.alerts, { status: AlertStatus.Open }).sort(activeAlertRecordSortingLogic);
    const inactiveAlertRecords = filter(this.alerts, { status: AlertStatus.Close }).sort(
      inactiveAlertRecordSortingLogic,
    );

    return [...activeAlertRecords, ...inactiveAlertRecords];
  }

  @computed get activeAlerts() {
    return filter(
      this.sortedAlerts,
      o => o.acknowledged === false && (o.inMaintenance === undefined || o.inMaintenance === false),
    );
  }

  @computed get shutdownAlerts() {
    return filter(this.activeAlerts, { severity: AlertLevels.Shutdown });
  }

  @computed get warningAlerts() {
    return filter(this.activeAlerts, { severity: AlertLevels.Warning });
  }

  @computed get serviceAlerts() {
    return filter(this.activeAlerts, { severity: AlertLevels.Service });
  }

  @computed get internalAlerts() {
    return filter(this.activeAlerts, { severity: AlertLevels.Internal });
  }

  @computed get archivedAlerts() {
    return filter(
      this.sortedAlerts,
      o => o.acknowledged === true || o.inMaintenance === undefined || o.inMaintenance === true,
    );
  }

  filterAlertsByDeviceId = deviceId => {
    const onDevices = this.rootStore.stationStore?.getOnDevicesForDevice(deviceId);
    let alertsOnADeviceAndItsOnDevices = filter(this.sortedAlerts, o =>
      find(o.affectedDevicesWithAttributes, { id: deviceId }),
    );

    onDevices?.forEach(device => {
      const alertOnDevicesOnDevices = filter(this.sortedAlerts, o =>
        find(o.affectedDevicesWithAttributes, { id: device.deviceId }),
      );
      alertsOnADeviceAndItsOnDevices = alertsOnADeviceAndItsOnDevices.concat(alertOnDevicesOnDevices);
    });

    return alertsOnADeviceAndItsOnDevices;
  };

  filterActiveAlerts = alerts =>
    filter(alerts, o => o.acknowledged === false && (o.inMaintenance === undefined || o.inMaintenance === false));

  filterShutdownAlerts = alerts => filter(alerts, { severity: AlertLevels.Shutdown });

  filterWarningAlerts = alerts => filter(alerts, { severity: AlertLevels.Warning });

  filterServiceAlerts = alerts => filter(alerts, { severity: AlertLevels.Service });

  filterInternalAlerts = alerts => filter(alerts, { severity: AlertLevels.Internal });

  filterOpenAlerts = alerts => filter(alerts, { status: AlertStatus.Open });

  filterClosedAlerts = alerts => filter(alerts, { status: AlertStatus.Close });

  filterArchivedAlerts = alerts =>
    filter(alerts, o => o.acknowledged === true || o.inMaintenance === undefined || o.inMaintenance === true);
}
